import { type Rng } from '@/core/rng';
import {
  parseZone,
  tileToWorldX,
  tileToWorldZ,
  type Zone,
  type ZoneSize,
} from '@/data/zone-schema';
import { type Renderer } from '@/gfx/renderer';
import {
  setInstance,
  uploadInstances,
  type InstanceBatch,
} from '@/gfx/webgl2/instance-batch';
import { m4, m4Compose, m4ComposeXYZ } from '@/math/mat4';
import { v3 } from '@/math/vec3';
import { createBoulderMesh } from '@/mesh/boulder';
import { createConeMesh } from '@/mesh/cone';
import { createCylinderMesh } from '@/mesh/cylinder';
import { createGroundTileMesh } from '@/mesh/ground-tile';
import { type AtlasRegion } from '@/texgen/atlas/shelf';

const TRUNK_HEIGHT = 1.6;
const TRUNK_RADIUS_BOTTOM = 0.22;
const TRUNK_RADIUS_TOP = 0.16;
const FOLIAGE_RADIUS = 0.7;
const FOLIAGE_HEIGHT = 1.6;
const FOLIAGE_BASE_RATIO = 0.6;
const TRUNK_SEGMENTS = 6;
const FOLIAGE_SEGMENTS = 6;

const NORMAL_GRASS_TINT = [1.0, 1.0, 1.0] as const;
const TALL_GRASS_TINT = [1.0, 0.92, 0.55] as const;
const TREE_TINT = [1.0, 1.0, 1.0] as const;
const BUSH_TINT = [0.9, 1.0, 0.85] as const;
const BLADE_TINT = [1.0, 1.1, 0.55] as const;
const STONE_TINT = [0.95, 0.92, 0.9] as const;
const ROCK_TINT = [0.85, 0.83, 0.82] as const;
const FIRE_TINT = [1.0, 0.55, 0.3] as const;

export interface ZoneRegions {
  grass: AtlasRegion;
  bark: AtlasRegion;
  leaf: AtlasRegion;
  stone: AtlasRegion;
  dirt: AtlasRegion;
}

export interface WorldBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export interface WorldCollider {
  x: number;
  z: number;
  radius: number;
}

export interface WorldTown {
  name: string;
  x: number;
  z: number;
}

export interface WorldPathPoint {
  x: number;
  z: number;
}

export interface WorldScene {
  zone: Zone;
  batches: readonly InstanceBatch[];
  bounds: WorldBounds;
  colliders: ReadonlyArray<WorldCollider>;
  paths: ReadonlyArray<ReadonlyArray<WorldPathPoint>>;
  towns: ReadonlyArray<WorldTown>;
  spawn: WorldPathPoint;
}

const fillUvRect = (out: Float32Array, r: AtlasRegion): void => {
  out[0] = r.u0;
  out[1] = r.v0;
  out[2] = r.u1 - r.u0;
  out[3] = r.v1 - r.v0;
};

// Per-tile rotation jitter — deterministic from tile index so reloads
// render identically.
const tileRotation = (tx: number, tz: number): number => {
  const h = (tx * 73856093) ^ (tz * 19349663);
  return ((h & 3) * Math.PI) / 2;
};

// Deterministic per-object scale jitter (sx, sz) for stones, in [0.85, 1.2].
// Keeps the natural variety the procedural grove had without baking it
// into the JSON, while letting editor users control just one number.
const stoneJitter = (i: number): { sx: number; sz: number } => {
  let h = (i * 2654435761) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  const a = ((h & 0xffff) / 0xffff) * 0.35 + 0.85;
  h = (h * 1597334677) >>> 0;
  const b = ((h & 0xffff) / 0xffff) * 0.35 + 0.85;
  return { sx: a, sz: b };
};

const rockJitter = (i: number): { sx: number; sy: number; sz: number } => {
  let h = (i * 2246822507) >>> 0;
  h = (h ^ (h >>> 11)) >>> 0;
  const a = ((h & 0xffff) / 0xffff) * 0.6 + 0.8;
  h = (h * 3266489917) >>> 0;
  const b = ((h & 0xffff) / 0xffff) * 0.4 + 0.4;
  h = (h * 374761393) >>> 0;
  const c = ((h & 0xffff) / 0xffff) * 0.6 + 0.8;
  return { sx: a, sy: b, sz: c };
};

export const parseZoneFromJson = (raw: unknown): Zone => parseZone(raw);

export interface BuildZoneCounts {
  trees: number;
  bushes: number;
  blades: number;
  flowers: number;
  rocks: number;
  bouldersAndStones: number;
}

const countObjects = (zone: Zone): BuildZoneCounts => {
  const c: BuildZoneCounts = {
    trees: 0,
    bushes: 0,
    blades: 0,
    flowers: 0,
    rocks: 0,
    bouldersAndStones: 0,
  };
  for (const o of zone.objects) {
    switch (o.type) {
      case 'tree':
        c.trees++;
        break;
      case 'bush':
        c.bushes++;
        break;
      case 'blade':
        c.blades++;
        break;
      case 'flower':
        c.flowers++;
        break;
      case 'rock':
        c.rocks++;
        break;
      case 'boulder':
      case 'town-stone':
      case 'fire-pit':
        c.bouldersAndStones++;
        break;
    }
  }
  return c;
};

const tileWorldBounds = (size: ZoneSize): WorldBounds => {
  const halfX = (size.width * size.tileSize) / 2;
  const halfZ = (size.height * size.tileSize) / 2;
  return {
    minX: -halfX + 1,
    maxX: halfX - 1,
    minZ: -halfZ + 1,
    maxZ: halfZ - 1,
  };
};

export const buildZone = (
  renderer: Renderer,
  regions: ZoneRegions,
  zone: Zone,
  _rng: Rng,
): WorldScene => {
  const groundMesh = renderer.uploadMesh(createGroundTileMesh(zone.size.tileSize));
  const trunkMesh = renderer.uploadMesh(
    createCylinderMesh(TRUNK_RADIUS_BOTTOM, TRUNK_RADIUS_TOP, TRUNK_HEIGHT, TRUNK_SEGMENTS),
  );
  const foliageMesh = renderer.uploadMesh(
    createConeMesh(FOLIAGE_RADIUS, FOLIAGE_HEIGHT, FOLIAGE_SEGMENTS),
  );
  const boulderMesh = renderer.uploadMesh(createBoulderMesh());

  const counts = countObjects(zone);
  const tileCount = zone.size.width * zone.size.height;

  const grassBatch = renderer.createInstanceBatch(groundMesh, tileCount);
  const dirtBatch = renderer.createInstanceBatch(groundMesh, tileCount);
  const trunkBatch = renderer.createInstanceBatch(trunkMesh, Math.max(counts.trees, 1));
  const foliageBatch = renderer.createInstanceBatch(foliageMesh, Math.max(counts.trees, 1));
  const bushBatch = renderer.createInstanceBatch(foliageMesh, Math.max(counts.bushes, 1));
  const bladeBatch = renderer.createInstanceBatch(foliageMesh, Math.max(counts.blades, 1));
  const flowerBatch = renderer.createInstanceBatch(foliageMesh, Math.max(counts.flowers, 1));
  const boulderBatch = renderer.createInstanceBatch(
    boulderMesh,
    Math.max(counts.bouldersAndStones, 1),
  );
  const rockBatch = renderer.createInstanceBatch(boulderMesh, Math.max(counts.rocks, 1));

  const grassUv = new Float32Array(4);
  const dirtUv = new Float32Array(4);
  const barkUv = new Float32Array(4);
  const leafUv = new Float32Array(4);
  const stoneUv = new Float32Array(4);
  fillUvRect(grassUv, regions.grass);
  fillUvRect(dirtUv, regions.dirt);
  fillUvRect(barkUv, regions.bark);
  fillUvRect(leafUv, regions.leaf);
  fillUvRect(stoneUv, regions.stone);

  const m = m4();
  const grassNormalTint = v3(NORMAL_GRASS_TINT[0], NORMAL_GRASS_TINT[1], NORMAL_GRASS_TINT[2]);
  const grassTallTint = v3(TALL_GRASS_TINT[0], TALL_GRASS_TINT[1], TALL_GRASS_TINT[2]);
  const treeTint = v3(TREE_TINT[0], TREE_TINT[1], TREE_TINT[2]);
  const bushTint = v3(BUSH_TINT[0], BUSH_TINT[1], BUSH_TINT[2]);
  const bladeTint = v3(BLADE_TINT[0], BLADE_TINT[1], BLADE_TINT[2]);
  const stoneTint = v3(STONE_TINT[0], STONE_TINT[1], STONE_TINT[2]);
  const rockTint = v3(ROCK_TINT[0], ROCK_TINT[1], ROCK_TINT[2]);
  const fireTint = v3(FIRE_TINT[0], FIRE_TINT[1], FIRE_TINT[2]);
  const customTint = v3();

  let grassCount = 0;
  let dirtCount = 0;
  for (let tz = 0; tz < zone.size.height; tz++) {
    for (let tx = 0; tx < zone.size.width; tx++) {
      const x = tileToWorldX(tx, zone.size);
      const z = tileToWorldZ(tz, zone.size);
      const idx = zone.tiles[tz * zone.size.width + tx]!;
      const kind = zone.tilePalette[idx]!;
      const rot = tileRotation(tx, tz);
      m4Compose(m, x, 0, z, rot, 1);
      if (kind === 'dirt' || kind === 'sand' || kind === 'stone') {
        setInstance(dirtBatch, dirtCount++, m, dirtUv, grassNormalTint);
      } else if (kind === 'tall-grass') {
        setInstance(grassBatch, grassCount++, m, grassUv, grassTallTint);
      } else {
        setInstance(grassBatch, grassCount++, m, grassUv, grassNormalTint);
      }
    }
  }

  const colliders: WorldCollider[] = [];
  let trunkIdx = 0;
  let foliageIdx = 0;
  let bushIdx = 0;
  let bladeIdx = 0;
  let flowerIdx = 0;
  let boulderIdx = 0;
  let rockIdx = 0;

  for (let i = 0; i < zone.objects.length; i++) {
    const o = zone.objects[i]!;
    const scale = o.scale ?? 1;
    const rotY = o.rotY ?? 0;
    if (o.type === 'tree') {
      m4Compose(m, o.x, 0, o.z, rotY, scale);
      setInstance(trunkBatch, trunkIdx++, m, barkUv, treeTint);
      m4Compose(m, o.x, TRUNK_HEIGHT * scale * FOLIAGE_BASE_RATIO, o.z, rotY, scale);
      setInstance(foliageBatch, foliageIdx++, m, leafUv, treeTint);
      colliders.push({ x: o.x, z: o.z, radius: TRUNK_RADIUS_BOTTOM * scale + 0.15 });
    } else if (o.type === 'bush') {
      m4Compose(m, o.x, 0, o.z, rotY, scale);
      setInstance(bushBatch, bushIdx++, m, leafUv, bushTint);
      colliders.push({ x: o.x, z: o.z, radius: FOLIAGE_RADIUS * scale * 0.45 });
    } else if (o.type === 'blade') {
      m4Compose(m, o.x, 0, o.z, rotY, scale);
      setInstance(bladeBatch, bladeIdx++, m, leafUv, bladeTint);
    } else if (o.type === 'flower') {
      m4Compose(m, o.x, 0, o.z, rotY, scale);
      const tint = o.tint;
      if (tint !== undefined) {
        customTint[0] = tint[0];
        customTint[1] = tint[1];
        customTint[2] = tint[2];
        setInstance(flowerBatch, flowerIdx++, m, leafUv, customTint);
      } else {
        setInstance(flowerBatch, flowerIdx++, m, leafUv, bladeTint);
      }
    } else if (o.type === 'boulder') {
      const j = stoneJitter(i);
      const sx = scale * j.sx;
      const sy = scale * (j.sx * 0.75);
      const sz = scale * j.sz;
      m4ComposeXYZ(m, o.x, sy, o.z, rotY, sx * 2, sy * 2, sz * 2);
      setInstance(boulderBatch, boulderIdx++, m, stoneUv, stoneTint);
      colliders.push({ x: o.x, z: o.z, radius: Math.max(sx, sz) + 0.1 });
    } else if (o.type === 'town-stone') {
      const j = stoneJitter(i);
      const sy = scale * j.sx;
      m4ComposeXYZ(m, o.x, sy, o.z, rotY, scale * 2, sy * 2, scale * 2);
      setInstance(boulderBatch, boulderIdx++, m, stoneUv, stoneTint);
      colliders.push({ x: o.x, z: o.z, radius: scale + 0.1 });
    } else if (o.type === 'fire-pit') {
      const j = stoneJitter(i);
      const sy = scale * j.sx;
      m4ComposeXYZ(m, o.x, sy, o.z, rotY, scale * 2, sy * 2, scale * 2);
      setInstance(boulderBatch, boulderIdx++, m, stoneUv, fireTint);
      colliders.push({ x: o.x, z: o.z, radius: scale + 0.1 });
    } else if (o.type === 'rock') {
      const j = rockJitter(i);
      const sx = scale * j.sx;
      const sy = scale * j.sy;
      const sz = scale * j.sz;
      m4ComposeXYZ(m, o.x, sy, o.z, rotY, sx * 2, sy * 2, sz * 2);
      setInstance(rockBatch, rockIdx++, m, stoneUv, rockTint);
    }
  }

  const gl = renderer.gl;
  uploadInstances(gl, grassBatch, grassCount);
  uploadInstances(gl, dirtBatch, dirtCount);
  uploadInstances(gl, trunkBatch, trunkIdx);
  uploadInstances(gl, foliageBatch, foliageIdx);
  uploadInstances(gl, bushBatch, bushIdx);
  uploadInstances(gl, bladeBatch, bladeIdx);
  uploadInstances(gl, flowerBatch, flowerIdx);
  uploadInstances(gl, boulderBatch, boulderIdx);
  uploadInstances(gl, rockBatch, rockIdx);

  return {
    zone,
    batches: [
      grassBatch,
      dirtBatch,
      trunkBatch,
      foliageBatch,
      bushBatch,
      bladeBatch,
      flowerBatch,
      boulderBatch,
      rockBatch,
    ],
    bounds: tileWorldBounds(zone.size),
    colliders,
    paths: zone.paths.map((p) => p.points.map((pt) => ({ x: pt.x, z: pt.z }))),
    towns: zone.towns.map((t) => ({ name: t.name, x: t.x, z: t.z })),
    spawn: { x: zone.spawn.x, z: zone.spawn.z },
  };
};
